/**
 * Authenticated document/image fetching for PHI-bearing endpoints.
 *
 * Protected timesheet-document routes require the httpOnly session
 * cookie AND the `X-Agency-Id` header — raw `<img src>` tags cannot
 * send the header, so every preview asset is fetched through the
 * shared axios client as a Blob and rendered via an object URL.
 * Object URLs are revoked on unmount/change so browser memory never
 * leaks across long review sessions. Nothing is written to
 * localStorage.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../services/api';

/** Fetch a protected asset as a Blob object URL. Caller must revoke. */
export const fetchBlobUrl = async (path) => {
  const res = await api.get(path, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};

/** Hook: authenticated blob URL with automatic revocation. */
export const useAuthedBlobUrl = (path, enabled = true) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path || !enabled) {
      setUrl(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(false);
    api
      .get(path, { responseType: 'blob' })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        } else {
          setUrl(objectUrl);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [path, enabled]);

  return { url, loading, error };
};

/** Hook: defer work until the element scrolls near the viewport. */
export const useInViewport = (rootMargin = '200px') => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin]);
  return [ref, visible];
};

/** Download the exact retained original through the audited endpoint. */
export const downloadTimesheetOriginal = async (timesheetId, filename) => {
  const res = await api.get(`/timesheets/${timesheetId}/document/download`, {
    responseType: 'blob',
  });
  const objectUrl = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'timesheet-original';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
};

/** Open the protected original in a new tab via a temporary blob URL. */
export const openTimesheetOriginal = async (timesheetId) => {
  const res = await api.get(`/timesheets/${timesheetId}/document/open`, {
    responseType: 'blob',
  });
  const objectUrl = URL.createObjectURL(res.data);
  window.open(objectUrl, '_blank', 'noopener');
  // Give the new tab time to load the blob before releasing it.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
};
